import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button, Paper, Skeleton } from '@mui/material';
import orderBy from 'lodash/orderBy';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ICreatePunchRequestDTO, IPunchAssignmentsItemDTO, IPunchDTO, IRequestResultDTO } from 'src/api/dtos';
import { IError } from 'src/api/types';
import { DepartmentPunchModal, JobPunchModal, RecentPunchesDrawer } from 'src/features/punches';
import {
  ClockLoopIcon,
  DepartmentIcon,
  EmptyState,
  PunchIcon,
  PunchInIcon,
  PunchOutIcon,
  ServerError500Icon,
  SuitcaseIcon,
} from 'src/shared/components';
import { DATE_TIME_FORMAT, DAY_DATE_FORMAT } from 'src/shared/constants';
import { FeaturesEnums, LocalStorageEnums, PunchFunctionEnums } from 'src/shared/enums';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { useAppDispatch, useLocalStorage, useThunkDispatch } from 'src/shared/hooks';
import { useTranslate, TranslationFunction } from 'src/shared/providers';
import { formatTime, formatDateTime } from 'src/shared/utils';
import { fetchDepartments } from 'src/store/departments/departments.actions';
import { fetchJobs } from 'src/store/jobs/jobs.actions';
import { fetchPunchAssignments } from 'src/store/punchAssignment/punchAssignment.actions';
import { createPunch, fetchRecentPunches, reset as resetPunches } from 'src/store/punches/punches.actions';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

const IP_ERROR_MESSAGE = 'ip address';
const BADGE_ERROR_MESSAGE = 'badge';

export const PunchCard: FC = () => {
  const dispatch = useAppDispatch();
  const dispatchThunk = useThunkDispatch();
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);
  const { data: departments } = useSelector((state: RootState) => state.departments);
  const { data: jobs } = useSelector((state: RootState) => state.jobs);
  const { data: punchAssignments, loading: punchAssignmentLoading } = useSelector(
    (state: RootState) => state.punchAssignments,
  );
  const {
    data: punches,
    loading: punchesLoading,
    submitting,
    error: punchesError,
  } = useSelector((state: RootState) => state.punches);

  const [currentPunchType, setCurrentPunchType] = useState<PunchFunctionEnums | undefined>();
  const [jobOpenDrawer, setJobOpenModal] = useState(false);
  const [departmentOpenDrawer, setDepartmentOpenModal] = useState(false);
  const [recentPunchOpenDrawer, setRecentPunchOpenDrawer] = useState(false);

  const hasPunchOut = punchAssignments.some(({ punchFunction }) => punchFunction === PunchFunctionEnums.OUT);

  const latestPunch = getLatestPunch(punches);

  const handleOpenDepartmentsModal = () => {
    dispatch(fetchDepartments());
    setDepartmentOpenModal(true);
  };

  const handleOpenDJobsModal = () => {
    dispatch(fetchJobs());
    setJobOpenModal(true);
  };

  const latestPunchLabels = useMemo((): { value?: string } => {
    if (!latestPunch) {
      return {};
    }

    switch (latestPunch.punchType) {
      case PunchFunctionEnums.IN:
        return {
          value: undefined,
        };
      case PunchFunctionEnums.OUT:
        return {
          value: undefined,
        };
      case PunchFunctionEnums.DEPT:
        return {
          value: t('punches.deptDetails'),
        };
      case PunchFunctionEnums.JOB:
        return {
          value: t('punches.jobDetails'),
        };
      default:
        return {};
    }
  }, [latestPunch]);

  useEffect(() => {
    dispatch(fetchRecentPunches());
    dispatch(fetchPunchAssignments());

    return () => {
      dispatch(resetPunches());
    };
  }, []);

  const getPunchErrorMessage = (error: IRequestResultDTO | undefined): string => {
    const isIpError = error?.validationErrors?.some((validationError) =>
      validationError.message.toLowerCase().includes(IP_ERROR_MESSAGE),
    );

    if (isIpError) {
      return t('punches.ipAddressIsNotPermitted');
    }

    const isBadgeError = error?.errorMessage?.toLowerCase().includes(BADGE_ERROR_MESSAGE);

    if (isBadgeError) {
      return t('punches.thereAreNoValidBadgesInTheSystemForYou').replace('\\n', '\n');
    }

    return t('global.oopsAnErrorOccurredWhileProcessingYourRequest');
  };

  const handleSubmit = async (punchFunction: PunchFunctionEnums, value?: string | string[]) => {
    setCurrentPunchType(punchFunction);

    const payload: ICreatePunchRequestDTO = {
      clockNumber: null,
      punchFunction: punchFunction,
      departmentNumber: punchFunction === PunchFunctionEnums.DEPT ? (value as string) : null,
      jobLevelSelectedValues: punchFunction === PunchFunctionEnums.JOB ? (value as string[]) : null,
    };

    try {
      const response = await dispatchThunk(createPunch(payload));

      switch (response.punchType) {
        case PunchFunctionEnums.IN:
          break;
        case PunchFunctionEnums.OUT:
          break;
        case PunchFunctionEnums.DEPT:
          setDepartmentOpenModal(false);
          break;
        case PunchFunctionEnums.JOB:
          setJobOpenModal(false);
          break;
        default:
        // no default
      }

      showSuccessToast(response, t, timeFormat);
    } catch (e) {
      const { data } = e as IError;

      const errorMessage = getPunchErrorMessage(data);
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  if (!punchAssignments || !punches || punchAssignmentLoading || punchesLoading) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  if (punchesError) {
    const errorMessage = getPunchErrorMessage(punchesError.data);

    return (
      <Paper className="h-100 p-4">
        <h4 className="pb-0 mb-0">{t('punches.punch')}</h4>
        <EmptyState className="m-0" message={errorMessage} icon={<ServerError500Icon />} />
      </Paper>
    );
  }

  return (
    <>
      <Paper id="punch-card" className="h-100 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2">
          <h4 className="pb-0 mb-0">{t('punches.punch')}</h4>
          <Button
            id="view-punch-details"
            className="w-unset"
            color="secondary"
            onClick={() => setRecentPunchOpenDrawer(true)}
            startIcon={<ClockLoopIcon />}
          >
            {t('punches.recentPunches')}
          </Button>
        </div>
        <div className="d-flex flex-column justify-content-between" style={{ height: 'calc(100% - 60px)' }}>
          <div className="mt-3">
            {latestPunch ? (
              <>
                <p>
                  {t('punches.lastPunchWasSubmittedAt')}{' '}
                  <strong>{formatDateTime(latestPunch.punchTimestamp, DAY_DATE_FORMAT, timeFormat)}</strong>
                </p>
                {!!latestPunch.value && (
                  <p>
                    {latestPunchLabels.value} <strong>{latestPunch.value}</strong>
                  </p>
                )}
                <p>
                  {t('punches.punchType')} <span className="fw-medium">{latestPunch.description}</span>
                </p>
              </>
            ) : (
              <div className="d-flex h-100 mt-3 align-items-center justify-content-start">
                <p className="fw-400 mb-0">{t('punches.noPunchesToShow')}</p>
              </div>
            )}
          </div>
          <div className="d-flex flex-column flex-sm-row justify-content-start flex-wrap">
            {punchAssignments.map((item: IPunchAssignmentsItemDTO) => {
              switch (item.punchFunction) {
                case PunchFunctionEnums.IN:
                  return (
                    <LoadingButton
                      key={item.punchFunction}
                      id="punch-in-btn"
                      className="me-2 mb-2"
                      loadingPosition="start"
                      loading={currentPunchType === PunchFunctionEnums.IN && submitting}
                      variant="contained"
                      startIcon={hasPunchOut ? <PunchInIcon /> : <PunchIcon />}
                      onClick={() => handleSubmit(item.punchFunction)}
                    >
                      {item.description}
                    </LoadingButton>
                  );
                case PunchFunctionEnums.OUT:
                  return (
                    <LoadingButton
                      key={item.punchFunction}
                      className="me-2 mb-2"
                      id="punch-out-btn"
                      loadingPosition="start"
                      loading={currentPunchType === PunchFunctionEnums.OUT && submitting}
                      variant="contained"
                      startIcon={<PunchOutIcon />}
                      onClick={() => handleSubmit(item.punchFunction)}
                    >
                      {item.description}
                    </LoadingButton>
                  );
                case PunchFunctionEnums.DEPT:
                  return (
                    <ComponentWithFeatures key={item.punchFunction} features={FeaturesEnums.DepartmentAllocation}>
                      <Button
                        className="me-2 mb-2"
                        id="punch-department-btn"
                        color="secondary"
                        onClick={handleOpenDepartmentsModal}
                        startIcon={<DepartmentIcon />}
                      >
                        {item.description}
                      </Button>
                    </ComponentWithFeatures>
                  );
                case PunchFunctionEnums.JOB:
                  return (
                    <ComponentWithFeatures key={item.punchFunction} features={FeaturesEnums.JobCosting}>
                      <Button
                        className="me-2 mb-2"
                        id="punch-job-btn"
                        color="secondary"
                        onClick={handleOpenDJobsModal}
                        startIcon={<SuitcaseIcon />}
                      >
                        {item.description}
                      </Button>
                    </ComponentWithFeatures>
                  );
                default:
                  return null;
              }
            })}
          </div>
        </div>
      </Paper>
      {!!jobs && (
        <JobPunchModal
          jobs={jobs}
          loading={currentPunchType === PunchFunctionEnums.JOB && submitting}
          open={jobOpenDrawer}
          onClose={() => setJobOpenModal(false)}
          onSubmitPunch={(values) => handleSubmit(PunchFunctionEnums.JOB, values)}
        />
      )}
      {!!departments && (
        <DepartmentPunchModal
          departments={departments}
          loading={currentPunchType === PunchFunctionEnums.DEPT && submitting}
          open={departmentOpenDrawer}
          onClose={() => setDepartmentOpenModal(false)}
          onSubmit={(value) => handleSubmit(PunchFunctionEnums.DEPT, value)}
        />
      )}
      {!!punches && (
        <RecentPunchesDrawer
          recentPunches={punches}
          open={recentPunchOpenDrawer}
          onClose={() => setRecentPunchOpenDrawer(false)}
        />
      )}
    </>
  );
};

const getLatestPunch = (punches: IPunchDTO[]): IPunchDTO | undefined => {
  if (!punches.length) {
    return;
  }

  const [latestPunch] = orderBy(punches, 'punchTimestamp', 'desc');

  return latestPunch;
};

const showSuccessToast = (
  { value, punchType, punchTimestamp }: IPunchDTO,
  t: TranslationFunction,
  timeFormat: string | undefined,
) => {
  const submitTime = formatTime(punchTimestamp, timeFormat, DATE_TIME_FORMAT);

  let message: ReactNode = (
    <span className="pb-0 mb-0">{t('punches.successYourPunchWasSubmittedAtPunchTime', { PunchTime: submitTime })}</span>
  );

  if (value && (punchType === PunchFunctionEnums.JOB || punchType === PunchFunctionEnums.DEPT)) {
    message = (
      <span className="pb-0 mb-0">
        {t('punches.successYourPunchWasSubmittedAtPunchTimeForPunchType', { PunchTime: submitTime, PunchType: value })}
      </span>
    );
  }

  toast.info(message, { type: toast.TYPE.SUCCESS });
};
